import { useCallback, useMemo } from "react";
import useSWR from "swr";
import { auth, vceFirestore } from "../../configs/FirebaseConfig";
import { FIRESTORE_PICKERS_TABLE } from "../../constants/FirestoreConstant";
import { BADGE_PLAN } from "../../functions/badgeCards";

/**
 * The picker register: `pickers/{BADGE-0001}` in VCE, which name belongs to which badge.
 *
 * The scanner app knows only the badge code - it puts it in every event as `pickerId` - so this is
 * where a code becomes a person: in the picker report, on the rounds and wherever the CMS shows
 * who picked. Written when badges are printed (functions/badgeCards planBadgeRegistration) and on
 * the Pickers page. A badge is never deleted, only switched off, so its history keeps its name.
 */

const pickersCollection = () => vceFirestore.collection(FIRESTORE_PICKERS_TABLE);

export const fetchPickers = async () => {
  const snapshot = await pickersCollection().get();
  return snapshot.docs.map((doc) => ({ ...doc.data(), code: doc.id }));
};

const currentUser = () => ({
  uid: (auth.currentUser && auth.currentUser.uid) || null,
  email: (auth.currentUser && auth.currentUser.email) || null,
});

const NO_PICKERS = [];

export const usePickers = () => {
  const { data, error, isLoading, mutate } = useSWR(["pickers"], fetchPickers, {
    revalidateOnFocus: true,
    dedupingInterval: 30000,
    // Refused reads (rules without the pickers block) do not get better by retrying
    shouldRetryOnError: false,
  });
  const pickers = data || NO_PICKERS;
  const byCode = useMemo(() => new Map(pickers.map((picker) => [picker.code, picker])), [pickers]);
  const nameOf = useCallback((code) => (code && byCode.has(code) ? byCode.get(code).name || null : null), [byCode]);

  /**
   * Write what planBadgeRegistration decided, before the badges are printed, in one transaction:
   * a code that someone else registered in the meantime refuses the whole print instead of being
   * overwritten.
   *
   * @param {Array<{code, name, kind}>} cards
   */
  const registerBadges = useCallback(
    async (cards) => {
      const now = Date.now();
      const by = currentUser();
      await vceFirestore.runTransaction(async (transaction) => {
        const references = cards.map((card) => pickersCollection().doc(card.code));
        const snapshots = await Promise.all(references.map((reference) => transaction.get(reference)));
        cards.forEach((card, index) => {
          const exists = snapshots[index].exists;
          if (card.kind === BADGE_PLAN.NEW && exists) throw new Error(`pickers/taken:${card.code}`);
          if (card.kind !== BADGE_PLAN.NEW && !exists) throw new Error(`pickers/missing:${card.code}`);
        });
        cards.forEach((card, index) => {
          if (card.kind === BADGE_PLAN.NEW) {
            transaction.set(references[index], {
              code: card.code,
              name: card.name,
              active: true,
              createdAt: now,
              createdBy: by,
              printedAt: now,
              updatedAt: now,
              updatedBy: by,
            });
          } else {
            transaction.update(references[index], { active: true, printedAt: now, updatedAt: now, updatedBy: by });
          }
        });
      });
      await mutate();
    },
    [mutate],
  );

  /** A new spelling or on/off. The code never changes: it is on the badge and in every event. */
  const updatePicker = useCallback(
    async (code, { name, active } = {}) => {
      const patch = { updatedAt: Date.now(), updatedBy: currentUser() };
      if (typeof name === "string") {
        if (!name.trim()) throw new Error("pickers/no-name");
        patch.name = name.trim();
      }
      if (typeof active === "boolean") patch.active = active;
      await pickersCollection().doc(code).update(patch);
      await mutate();
    },
    [mutate],
  );

  return { pickers, error, isLoading, nameOf, registerBadges, updatePicker, refresh: mutate };
};
